import { useState } from 'react';
import {
  makeStyles,
  mergeClasses,
  tokens,
  Text,
  Button,
  Card,
  Badge,
  Tooltip,
} from '@coreai/fluentui-react';
import {
  CheckmarkRegular,
  DismissRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  WarningRegular,
  InfoRegular,
  ArrowUndoRegular,
} from '@fluentui/react-icons';
import type { WorkflowProposal, DiffSummary as DiffSummaryType } from '@/types/proposal';
import { formatPathForDisplay, isLargeDiff } from '@/utils/json';
import { ConnectionApprovalCard } from './ConnectionApprovalCard';
import { useTelemetry } from '@/hooks/useTelemetry';

const useStyles = makeStyles({
  card: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusMedium,
    boxShadow: tokens.shadow4,
    overflow: 'hidden',
    padding: '0',
  },
  cardPending: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },
  cardInvalid: {
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
  },
  cardNoChange: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    opacity: 0.8,
  },
  cardApproved: {
    border: `1px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  cardRejected: {
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    opacity: 0.6,
  },
  header: {
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    backgroundColor: tokens.colorNeutralBackground2,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerContent: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: tokens.spacingHorizontalS,
  },
  headerTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  body: {
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
  },
  diffSummary: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  diffLine: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
  },
  diffAdded: {
    color: tokens.colorPaletteGreenForeground1,
  },
  diffRemoved: {
    color: tokens.colorPaletteRedForeground1,
  },
  diffModified: {
    color: tokens.colorPaletteYellowForeground1,
  },
  diffPath: {
    wordBreak: 'break-all',
  },
  diffItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
    paddingBottom: tokens.spacingVerticalXS,
    borderBottom: `1px solid ${tokens.colorNeutralStroke3}`,
    marginBottom: tokens.spacingVerticalXS,
  },
  diffItemHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
  },
  diffValue: {
    fontSize: tokens.fontSizeBase200,
    fontFamily: tokens.fontFamilyMonospace,
    borderRadius: tokens.borderRadiusSmall,
    whiteSpace: 'pre-wrap',
    wordBreak: 'break-all',
    maxHeight: '80px',
    overflowY: 'auto',
    display: 'flex',
  },
  diffValueContent: {
    display: 'flex',
    width: '100%',
  },
  lineNumbers: {
    display: 'flex',
    flexDirection: 'column',
    padding: tokens.spacingVerticalXXS,
    paddingRight: tokens.spacingHorizontalS,
    borderRight: `1px solid ${tokens.colorNeutralStroke3}`,
    color: tokens.colorNeutralForeground4,
    userSelect: 'none',
    textAlign: 'right',
    minWidth: '24px',
  },
  lineNumber: {
    lineHeight: '1.4',
  },
  valueText: {
    flex: 1,
    padding: tokens.spacingVerticalXXS,
    paddingInline: tokens.spacingHorizontalS,
  },
  oldValue: {
    backgroundColor: `color-mix(in srgb, ${tokens.colorPaletteRedBackground1} 50%, transparent)`,
    color: tokens.colorPaletteRedForeground1,
  },
  newValue: {
    backgroundColor: `color-mix(in srgb, ${tokens.colorPaletteGreenBackground1} 50%, transparent)`,
    color: tokens.colorPaletteGreenForeground1,
  },
  statsRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
    fontSize: tokens.fontSizeBase200,
  },
  statBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXXS,
  },
  actions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  expandButton: {
    marginTop: tokens.spacingVerticalXS,
  },
  errorMessage: {
    color: tokens.colorPaletteRedForeground1,
    fontSize: tokens.fontSizeBase200,
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
  },
  infoMessage: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    display: 'flex',
    alignItems: 'flex-start',
    gap: tokens.spacingHorizontalXS,
  },
  resultMessage: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    paddingInline: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase200,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground3,
  },
  approvedMessage: {
    color: tokens.colorPaletteGreenForeground1,
  },
  rejectedMessage: {
    color: tokens.colorNeutralForeground3,
  },
  collapsedSummary: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
  },
  changeActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    marginTop: tokens.spacingVerticalXS,
  },
  changeActionButton: {
    minWidth: 'auto',
    padding: `${tokens.spacingVerticalXXS} ${tokens.spacingHorizontalS}`,
  },
  changeApproved: {
    backgroundColor: `color-mix(in srgb, ${tokens.colorPaletteGreenBackground1} 40%, transparent)`,
    borderLeft: `3px solid ${tokens.colorPaletteGreenBorder1}`,
  },
  changeRejected: {
    backgroundColor: tokens.colorNeutralBackground3,
    borderLeft: `3px solid ${tokens.colorNeutralStroke1}`,
    opacity: 0.6,
  },
  changePending: {
    borderLeft: `3px solid ${tokens.colorBrandStroke1}`,
  },
  changeStatusBadge: {
    fontSize: tokens.fontSizeBase100,
    marginLeft: 'auto',
  },
  lineNumberBadge: {
    fontSize: tokens.fontSizeBase100,
    color: tokens.colorNeutralForeground4,
    fontFamily: tokens.fontFamilyMonospace,
    marginLeft: tokens.spacingHorizontalXS,
  },
  changeIndex: {
    minWidth: '24px',
    height: '24px',
    borderRadius: tokens.borderRadiusCircular,
    backgroundColor: tokens.colorBrandBackground2,
    color: tokens.colorBrandForeground1,
    fontSize: tokens.fontSizeBase200,
    fontWeight: tokens.fontWeightBold,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  bulkActions: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  bulkActionGroup: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
  },
  // Blue border for Approve buttons
  approveButton: {
    border: `1px solid ${tokens.colorBrandStroke1}`,
    color: tokens.colorBrandForeground1,
  },
  // Red border for Reject/Discard buttons
  rejectButton: {
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    color: tokens.colorPaletteRedForeground1,
  },
});

interface ProposalCardProps {
  proposal: WorkflowProposal;
  onApprove: () => void;
  onReject: () => void;
  onApproveChange?: (changeId: string) => void;
  onRejectChange?: (changeId: string) => void;
  onUndoChange?: (changeId: string) => void;
  onApplyApproved?: () => void;
  // Connection assignment callbacks
  onApproveConnectionAssignment?: (requirementId: string, referenceKey: string) => void;
  onRejectConnectionAssignment?: (requirementId: string) => void;
  onApproveAllConnectionAssignments?: () => void;
  onSkipAllConnectionAssignments?: () => void;
}

const MAX_VISIBLE_CHANGES = 5;

/**
 * Format a value for display in the diff
 */
const formatValue = (value: unknown): string => {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') return `"${value}"`;
  if (typeof value === 'object') {
    try {
      const json = JSON.stringify(value, null, 2);
      // Truncate very long values
      if (json.length > 200) {
        return json.substring(0, 200) + '...';
      }
      return json;
    } catch {
      return String(value);
    }
  }
  return String(value);
};

/**
 * Extract the key name from a path (the last non-numeric segment)
 */
const extractKeyFromPath = (path: string): string | undefined => {
  const parts = path.split('.');
  // Find the last non-numeric part (skip array indices)
  for (let i = parts.length - 1; i >= 0; i--) {
    const part = parts[i];
    if (part && !/^\d+$/.test(part)) {
      return part;
    }
  }
  return undefined;
};

/**
 * Format a value with its key for display (e.g., "interval": 12)
 */
const formatValueWithKey = (value: unknown, key: string | undefined): string => {
  const formattedValue = formatValue(value);
  if (!key) {
    return formattedValue;
  }
  // For objects/arrays, show key on first line, value indented below
  if (typeof value === 'object' && value !== null) {
    return `"${key}": ${formattedValue}`;
  }
  // For primitives, show as single line
  return `"${key}": ${formattedValue}`;
};

/**
 * Renders a value with line numbers (only shows line numbers for multi-line values)
 * Shows the full JSON key-value pair (e.g., "interval": 12) instead of just the value
 */
const ValueWithLineNumbers = ({ value, path, className }: { value: unknown; path: string; className: string }) => {
  const styles = useStyles();
  const key = extractKeyFromPath(path);
  const formattedValue = formatValueWithKey(value, key);
  const lines = formattedValue.split('\n');
  const isMultiLine = lines.length > 1;

  return (
    <div className={mergeClasses(styles.diffValue, className)}>
      <div className={styles.diffValueContent}>
        {/* Only show line numbers for multi-line values */}
        {isMultiLine && (
          <div className={styles.lineNumbers}>
            {lines.map((_, index) => (
              <span key={index} className={styles.lineNumber}>
                {index + 1}
              </span>
            ))}
          </div>
        )}
        <div className={styles.valueText}>
          {formattedValue}
        </div>
      </div>
    </div>
  );
};

/**
 * Renders a summary of diff changes with expandable details and individual actions
 */
const DiffSummaryView = ({
  diff,
  expanded,
  onToggleExpand,
  onApproveChange,
  onRejectChange,
  onUndoChange,
}: {
  diff: WorkflowProposal['diff'];
  expanded: boolean;
  onToggleExpand: () => void;
  onApproveChange?: ((changeId: string) => void) | undefined;
  onRejectChange?: ((changeId: string) => void) | undefined;
  onUndoChange?: ((changeId: string) => void) | undefined;
}) => {
  const styles = useStyles();
  const { summary, stats } = diff;

  const largeDiff = isLargeDiff(diff, 0);
  const visibleChanges = expanded ? summary : summary.slice(0, MAX_VISIBLE_CHANGES);
  const hiddenCount = summary.length - MAX_VISIBLE_CHANGES;

  const getIcon = (type: DiffSummaryType['type']) => {
    switch (type) {
      case 'added': return '+';
      case 'removed': return '-';
      case 'modified': return '~';
    }
  };

  const getStyle = (type: DiffSummaryType['type']) => {
    switch (type) {
      case 'added': return styles.diffAdded;
      case 'removed': return styles.diffRemoved;
      case 'modified': return styles.diffModified;
    }
  };

  const getChangeStatusClass = (status: DiffSummaryType['status']) => {
    switch (status) {
      case 'approved': return styles.changeApproved;
      case 'rejected': return styles.changeRejected;
      default: return styles.changePending;
    }
  };

  return (
    <div className={styles.diffSummary}>
      {/* Stats row */}
      <div className={styles.statsRow}>
        {stats.added > 0 && (
          <span className={mergeClasses(styles.statBadge, styles.diffAdded)}>
            +{stats.added} {stats.added === 1 ? 'addition' : 'additions'}
          </span>
        )}
        {stats.removed > 0 && (
          <span className={mergeClasses(styles.statBadge, styles.diffRemoved)}>
            -{stats.removed} {stats.removed === 1 ? 'removal' : 'removals'}
          </span>
        )}
        {stats.modified > 0 && (
          <span className={mergeClasses(styles.statBadge, styles.diffModified)}>
            ~{stats.modified} {stats.modified === 1 ? 'change' : 'changes'}
          </span>
        )}
      </div>

      {/* Large diff warning */}
      {largeDiff && !expanded && (
        <div className={styles.infoMessage}>
          <InfoRegular />
          <span>Large diff with {stats.total} changes. Expand to see all.</span>
        </div>
      )}

      {/* Change list with values */}
      {visibleChanges.map((change, index) => (
        <div
          key={change.id}
          className={mergeClasses(styles.diffItem, getChangeStatusClass(change.status))}
        >
          {/* Path header with status */}
          <div className={styles.diffItemHeader}>
            <span className={styles.changeIndex}>{index + 1}</span>
            <Tooltip content={change.path} relationship="label">
              <span className={getStyle(change.type)}>
                {getIcon(change.type)} {formatPathForDisplay(change.path)}
              </span>
            </Tooltip>
            {change.lineNumber !== undefined && (
              <span className={styles.lineNumberBadge}>
                L{change.lineNumber}
              </span>
            )}
            {change.status !== 'pending' && (
              <Badge
                appearance="outline"
                color={change.status === 'approved' ? 'success' : 'subtle'}
                size="small"
                className={styles.changeStatusBadge}
              >
                {change.status === 'approved' ? '✓' : '✗'}
              </Badge>
            )}
          </div>

          {/* Show old value for removed/modified */}
          {(change.type === 'removed' || change.type === 'modified') && change.oldValue !== undefined && (
            <ValueWithLineNumbers value={change.oldValue} path={change.path} className={styles.oldValue} />
          )}

          {/* Show new value for added/modified */}
          {(change.type === 'added' || change.type === 'modified') && change.newValue !== undefined && (
            <ValueWithLineNumbers value={change.newValue} path={change.path} className={styles.newValue} />
          )}

          {/* Individual change actions - show Approve/Reject for pending */}
          {change.status === 'pending' && onApproveChange && onRejectChange && (
            <div className={styles.changeActions}>
              <Button
                appearance="subtle"
                size="small"
                icon={<CheckmarkRegular />}
                className={mergeClasses(styles.changeActionButton, styles.approveButton)}
                onClick={() => onApproveChange(change.id)}
              >
                Approve
              </Button>
              <Button
                appearance="subtle"
                size="small"
                icon={<DismissRegular />}
                className={mergeClasses(styles.changeActionButton, styles.rejectButton)}
                onClick={() => onRejectChange(change.id)}
              >
                Reject
              </Button>
            </div>
          )}

          {/* Show Undo button for decided changes (approved/rejected) */}
          {change.status !== 'pending' && onUndoChange && (
            <div className={styles.changeActions}>
              <Button
                appearance="subtle"
                size="small"
                icon={<ArrowUndoRegular />}
                className={styles.changeActionButton}
                onClick={() => onUndoChange(change.id)}
              >
                Undo
              </Button>
            </div>
          )}
        </div>
      ))}

      {/* Expand/collapse button */}
      {hiddenCount > 0 && (
        <Button
          appearance="subtle"
          size="small"
          icon={expanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
          onClick={onToggleExpand}
          className={styles.expandButton}
        >
          {expanded ? 'Show less' : `Show ${hiddenCount} more`}
        </Button>
      )}
    </div>
  );
};

export const ProposalCard = ({
  proposal,
  onApprove,
  onReject,
  onApproveChange,
  onRejectChange,
  onUndoChange,
  onApplyApproved,
  onApproveConnectionAssignment,
  onRejectConnectionAssignment,
  onApproveAllConnectionAssignments,
  onSkipAllConnectionAssignments,
}: ProposalCardProps) => {
  const styles = useStyles();
  const { trackEvent } = useTelemetry();
  const [expanded, setExpanded] = useState(false);

  // Count changes by status
  const approvedCount = proposal.diff.summary.filter((c) => c.status === 'approved').length;
  const hasPerChangeReview = !!onApproveChange;

  const getCardClass = () => {
    switch (proposal.status) {
      case 'pending': return `${styles.card} ${styles.cardPending}`;
      case 'invalid': return `${styles.card} ${styles.cardInvalid}`;
      case 'no-change': return `${styles.card} ${styles.cardNoChange}`;
      case 'approved': return `${styles.card} ${styles.cardApproved}`;
      case 'rejected': return `${styles.card} ${styles.cardRejected}`;
      default: return styles.card;
    }
  };

  const getTitle = () => {
    if (proposal.type === 'workflow') {
      // Multi-node scoped edit — show the node names
      if (proposal.targetNames && proposal.targetNames.length > 0) {
        return `Editing ${proposal.targetNames.join(', ')}`;
      }
      return 'Workflow Update';
    }
    // type === 'node': single-node scoped edit
    return `Editing ${proposal.targetName || 'Unknown'}`;
  };

  const getStatusBadge = () => {
    switch (proposal.status) {
      case 'pending':
        return <Badge appearance="filled" color="brand">Review</Badge>;
      case 'invalid':
        return <Badge appearance="filled" color="danger">Invalid</Badge>;
      case 'no-change':
        return <Badge appearance="filled" color="subtle">No Changes</Badge>;
      case 'approved':
        return <Badge appearance="filled" color="success">Approved</Badge>;
      case 'rejected':
        return <Badge appearance="filled" color="subtle">Rejected</Badge>;
    }
  };

  // No-change state
  if (proposal.status === 'no-change') {
    return (
      <Card className={getCardClass()}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Text weight="semibold">{getTitle()}</Text>
            </div>
            {getStatusBadge()}
          </div>
        </div>
        <div className={styles.body}>
          <div className={styles.infoMessage}>
            <InfoRegular />
            <span>No changes detected. The proposed configuration is identical to the current one.</span>
          </div>
        </div>
      </Card>
    );
  }

  // Invalid state
  if (proposal.status === 'invalid') {
    return (
      <Card className={getCardClass()}>
        <div className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerTitle}>
              <Text weight="semibold">{getTitle()}</Text>
            </div>
            {getStatusBadge()}
          </div>
        </div>
        <div className={styles.body}>
          <div className={styles.errorMessage}>
            <WarningRegular />
            <span>{proposal.validationError || 'Invalid JSON output'}</span>
          </div>
        </div>
        <div className={styles.actions}>
          <Button
            appearance="secondary"
            size="small"
            onClick={onReject}
          >
            Dismiss
          </Button>
        </div>
      </Card>
    );
  }

  // Approved state - compact single line with counts
  if (proposal.status === 'approved') {
    const approvedCount = proposal.diff.summary.filter((c) => c.status === 'approved').length;
    const rejectedCount = proposal.diff.summary.filter((c) => c.status === 'rejected').length;

    let message: string;
    if (rejectedCount > 0) {
      message = `Applied ${approvedCount} change${approvedCount !== 1 ? 's' : ''}, rejected ${rejectedCount}`;
    } else {
      message = `Applied ${approvedCount} change${approvedCount !== 1 ? 's' : ''} to canvas`;
    }

    return (
      <div className={mergeClasses(styles.resultMessage, styles.approvedMessage)}>
        <CheckmarkRegular />
        <span>{message}</span>
      </div>
    );
  }

  // Rejected state - compact single line
  if (proposal.status === 'rejected') {
    return (
      <div className={mergeClasses(styles.resultMessage, styles.rejectedMessage)}>
        <DismissRegular />
        <span>Changes discarded</span>
      </div>
    );
  }

  // Pending state - show full diff and actions
  return (
    <Card className={getCardClass()}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <div className={styles.headerTitle}>
            <Text weight="semibold">{getTitle()}</Text>
          </div>
          {getStatusBadge()}
        </div>
      </div>
      <div className={styles.body}>
        <DiffSummaryView
          diff={proposal.diff}
          expanded={expanded}
          onToggleExpand={() => setExpanded(!expanded)}
          onApproveChange={onApproveChange}
          onRejectChange={onRejectChange}
          onUndoChange={onUndoChange}
        />

        {/* Connection assignment UI for workflow proposals with connection requirements */}
        {proposal.connectionMatching &&
          (proposal.connectionMatching.needsApproval.length > 0 ||
            proposal.connectionMatching.noConnectionAvailable.length > 0) &&
          onApproveConnectionAssignment &&
          onRejectConnectionAssignment &&
          onApproveAllConnectionAssignments &&
          onSkipAllConnectionAssignments && (
            <ConnectionApprovalCard
              connectionMatching={proposal.connectionMatching}
              onApprove={onApproveConnectionAssignment}
              onReject={onRejectConnectionAssignment}
              onApproveAll={onApproveAllConnectionAssignments}
              onSkipAll={onSkipAllConnectionAssignments}
            />
          )}
      </div>
      <div className={styles.actions}>
        <div className={styles.bulkActions}>
          {/* Spacer for right alignment */}
          <div />

          {/* Apply/Discard buttons */}
          <div className={styles.bulkActionGroup}>
            <Button
              appearance="primary"
              size="small"
              icon={<CheckmarkRegular />}
              onClick={() => {
                trackEvent('agent.chat.proposal_accepted');
                (approvedCount > 0 && onApplyApproved ? onApplyApproved : onApprove)();
              }}
              disabled={hasPerChangeReview && approvedCount === 0}
            >
              Apply{approvedCount > 0 ? ` (${approvedCount})` : ''}
            </Button>
            <Button
              appearance="subtle"
              size="small"
              icon={<DismissRegular />}
              className={styles.rejectButton}
              onClick={() => {
                trackEvent('agent.chat.proposal_rejected');
                onReject();
              }}
            >
              Discard
            </Button>
          </div>
        </div>
      </div>
    </Card>
  );
};
